import React, { useEffect, useState, useRef, useLayoutEffect, useMemo } from "react";
import "./CustomCursor.css";

const MOBILE_USER_AGENT_REGEX =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
const applyPosition = (ref, x, y) => {
  if (ref.current) {
    ref.current.style.left = `${x}px`;
    ref.current.style.top = `${y}px`;
  }
};

const CustomCursor = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  const [clickRipples, setClickRipples] = useState([]);
  const [isVisible, setIsVisible] = useState(true);
  const cursorRef = useRef(null);
  const trailRef = useRef(null);
  const animationFrameRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  useLayoutEffect(() => {
    applyPosition(cursorRef, 0, 0);
    applyPosition(trailRef, 0, 0);
  }, []);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice =
        MOBILE_USER_AGENT_REGEX.test(navigator.userAgent) ||
        window.matchMedia("(max-width: 768px) and (pointer: coarse)").matches;
      setIsMobile(isMobileDevice);
      // Hide cursor initially on mobile
      if (isMobileDevice) {
        setIsVisible(false);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile) {
      // Mobile: Show cursor only on touch/click
      const handleTouchStart = (e) => {
        const touch = e.touches[0];
        if (touch) {
          setIsVisible(true);
          applyPosition(cursorRef, touch.clientX, touch.clientY);
          applyPosition(trailRef, touch.clientX, touch.clientY);
          setIsClicking(true);

          // Create ripple effect
          const ripple = {
            id: Date.now(),
            x: touch.clientX,
            y: touch.clientY,
          };
          setClickRipples((prev) => [...prev, ripple]);
          setTimeout(() => {
            setClickRipples((prev) => prev.filter((r) => r.id !== ripple.id));
          }, 600);

          // Clear any existing hide timeout
          if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
          }
        }
      };

      const handleTouchMove = (e) => {
        const touch = e.touches[0];
        if (touch) {
          applyPosition(cursorRef, touch.clientX, touch.clientY);
          applyPosition(trailRef, touch.clientX, touch.clientY);
        }
      };

      const handleTouchEnd = () => {
        setIsClicking(false);
        // Hide cursor after 500ms on mobile
        hideTimeoutRef.current = setTimeout(() => {
          setIsVisible(false);
        }, 500);
      };

      const handleClick = (e) => {
        setIsVisible(true);
        applyPosition(cursorRef, e.clientX, e.clientY);
        applyPosition(trailRef, e.clientX, e.clientY);
        setIsClicking(true);

        const ripple = {
          id: Date.now(),
          x: e.clientX,
          y: e.clientY,
        };
        setClickRipples((prev) => [...prev, ripple]);
        setTimeout(() => {
          setClickRipples((prev) => prev.filter((r) => r.id !== ripple.id));
        }, 600);

        setTimeout(() => {
          setIsClicking(false);
          hideTimeoutRef.current = setTimeout(() => {
            setIsVisible(false);
          }, 500);
        }, 200);
      };

      document.addEventListener("touchstart", handleTouchStart);
      document.addEventListener("touchmove", handleTouchMove);
      document.addEventListener("touchend", handleTouchEnd);
      document.addEventListener("click", handleClick);

      return () => {
        document.removeEventListener("touchstart", handleTouchStart);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
        document.removeEventListener("click", handleClick);
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }
      };
    } else {
      // Desktop: Normal cursor tracking
      let mouseX = 0;
      let mouseY = 0;
      let cursorX = 0;
      let cursorY = 0;
      let trailX = 0;
      let trailY = 0;
      let velocityX = 0;
      let velocityY = 0;

      const updateCursorPosition = (e) => {
        mouseX = e.clientX;
        mouseY = e.clientY;
      };

      const animate = () => {
        // Smooth cursor movement with spring physics
        const dx = mouseX - cursorX;
        const dy = mouseY - cursorY;

        // Calculate velocity for smoother movement
        velocityX += dx * 0.1;
        velocityY += dy * 0.1;
        velocityX *= 0.7; // Friction
        velocityY *= 0.7;

        cursorX += velocityX;
        cursorY += velocityY;

        // Smooth trailing effect with improved spring physics
        trailX += (cursorX - trailX) * 0.12;
        trailY += (cursorY - trailY) * 0.12;

        applyPosition(cursorRef, cursorX, cursorY);
        applyPosition(trailRef, trailX, trailY);
        animationFrameRef.current = requestAnimationFrame(animate);
      };

      animate();

      const handleMouseOver = (e) => {
        const target = e.target;
        if (
          target.tagName === "A" ||
          target.tagName === "BUTTON" ||
          target.closest("a") ||
          target.closest("button") ||
          target.closest(".tech-icons") ||
          target.closest(".project-card-view") ||
          target.closest(".home-social-icons") ||
          target.closest(".btn") ||
          target.closest(".nav-link")
        ) {
          setIsHovering(true);
        } else {
          setIsHovering(false);
        }
      };

      const handleMouseDown = (e) => {
        setIsClicking(true);
        // Create ripple effect
        const ripple = {
          id: Date.now(),
          x: e.clientX,
          y: e.clientY,
        };
        setClickRipples((prev) => [...prev, ripple]);
        // Remove ripple after animation
        setTimeout(() => {
          setClickRipples((prev) => prev.filter((r) => r.id !== ripple.id));
        }, 600);
      };

      const handleMouseUp = () => {
        setIsClicking(false);
      };

      window.addEventListener("mousemove", updateCursorPosition);
      document.addEventListener("mouseover", handleMouseOver);
      document.addEventListener("mousedown", handleMouseDown);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        window.removeEventListener("mousemove", updateCursorPosition);
        document.removeEventListener("mouseover", handleMouseOver);
        document.removeEventListener("mousedown", handleMouseDown);
        document.removeEventListener("mouseup", handleMouseUp);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }
  }, [isMobile]);

  const trailStyle = useMemo(
    () => ({
      opacity: isVisible ? 1 : 0,
      transition: isMobile ? "opacity 0.3s ease" : "none",
    }),
    [isVisible, isMobile]
  );

  const cursorStyle = trailStyle; // identical shape/values, share the reference

  return (
    <>
      {/* Trailing bubble */}
      <div ref={trailRef} className="cursor-trail" style={trailStyle} />
      {/* Main cursor bubble */}
      <div
        ref={cursorRef}
        className={`custom-cursor ${isHovering ? "cursor-hover" : ""} ${
          isClicking ? "cursor-click" : ""
        }`}
        style={cursorStyle}
      >
        <div className="cursor-pulse"></div>
        {/* Wavy effect layers */}
        <div className="wave-layer wave-layer-1"></div>
        <div className="wave-layer wave-layer-2"></div>
        <div className="wave-layer wave-layer-3"></div>
      </div>
      {/* Click ripples */}
      {clickRipples.map((ripple) => (
        <div
          key={ripple.id}
          className="click-ripple"
          style={{
            left: `${ripple.x}px`,
            top: `${ripple.y}px`,
          }}
        />
      ))}
    </>
  );
};

export default CustomCursor;
